import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Banknote, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle2, AlertCircle,
} from "lucide-react";

/**
 * Per-tenant Bank Reconciliation viewer.
 *
 * Stage 1 of the multi-tenant Xero rollout. Sidebar (left) lists every
 * bank account in the chart of accounts; main pane shows the chronological
 * transaction list for the selected account, grouped by day with a
 * running daily total.
 */
export default function XeroBankRecPage() {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [txns, setTxns] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Load tenant + bank accounts
  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const [t, a] = await Promise.all([
        api.get("/integrations/xero/tenants"),
        api.get(`/integrations/xero/tenants/${tenantId}/bank-accounts`),
      ]);
      const matchedTenant = (t.data || []).find((x) => x.tenant_id === tenantId);
      setTenant(matchedTenant || { tenant_id: tenantId, tenant_name: tenantId });
      setAccounts(a.data?.accounts || []);
      if (!selectedId && (a.data?.accounts || []).length > 0) {
        setSelectedId(a.data.accounts[0].account_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load bank accounts");
    } finally { setLoadingAccounts(false); }
  }, [tenantId, selectedId]);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const loadTxns = useCallback(async () => {
    if (!selectedId) return;
    setLoadingTxns(true);
    try {
      const params = new URLSearchParams({ bank_account_id: selectedId, page: String(page) });
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const { data } = await api.get(
        `/integrations/xero/tenants/${tenantId}/bank-transactions?${params}`);
      setTxns(data.transactions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load transactions");
    } finally { setLoadingTxns(false); }
  }, [selectedId, page, fromDate, toDate, tenantId]);
  useEffect(() => { loadTxns(); }, [loadTxns]);

  // Group by day for display
  const grouped = useMemo(() => {
    const byDay = {};
    for (const t of txns) {
      const day = (t.date || "").slice(0, 10);
      if (!day) continue;
      (byDay[day] = byDay[day] || []).push(t);
    }
    return Object.entries(byDay).sort(([a], [b]) => b.localeCompare(a));
  }, [txns]);

  const selectedAccount = accounts.find((a) => a.account_id === selectedId);

  return (
    <div className="p-6 max-w-7xl" data-testid="xero-bank-rec-page">
      <Link to="/integrations/xero" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="xero-bank-rec-back">
        <ArrowLeft className="h-3 w-3" /> Back to Xero
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#13B5EA]/10">
          <Banknote className="h-6 w-6 text-[#13B5EA]" />
        </div>
        <div className="flex-1">
          <div className="label-overline">XERO · BANK RECONCILIATION</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            {tenant?.tenant_name || "…"}
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Every bank transaction across every account, grouped by day. Latest first.
          </p>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={() => { loadAccounts(); loadTxns(); }}
          data-testid="xero-bank-rec-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* ── Sidebar: bank accounts list ── */}
        <Card className="rounded-sm border-zinc-200 h-fit lg:sticky lg:top-4">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-zinc-100 font-display font-bold text-sm">
              Bank accounts ({accounts.length})
            </div>
            {loadingAccounts ? (
              <div className="p-4 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
            ) : accounts.length === 0 ? (
              <div className="p-4 text-sm italic text-zinc-500">No bank accounts found.</div>
            ) : accounts.map((a) => (
              <button key={a.account_id} type="button"
                onClick={() => { setSelectedId(a.account_id); setPage(1); }}
                className={`w-full text-left px-4 py-3 border-b border-zinc-100 last:border-b-0 text-sm transition-colors ${
                  a.account_id === selectedId
                    ? "bg-[#13B5EA]/10 border-l-2 border-l-[#13B5EA]"
                    : "hover:bg-zinc-50"
                }`}
                data-testid={`xero-bank-account-${a.account_id}`}>
                <div className="font-display font-bold truncate">{a.name}</div>
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5 flex items-center gap-2">
                  <span>{a.currency || "—"}</span>
                  {a.code && <Badge variant="outline" className="rounded-sm text-[9px] py-0">{a.code}</Badge>}
                  {a.bank_account_number && <span className="truncate">{a.bank_account_number}</span>}
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* ── Main pane: transactions grouped by day ── */}
        <div className="space-y-4">
          {/* Filters */}
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-4 flex flex-wrap items-end gap-3">
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                From
                <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                  className="mt-1 h-9 text-xs font-mono" data-testid="xero-bank-rec-from" />
              </label>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                To
                <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                  className="mt-1 h-9 text-xs font-mono" data-testid="xero-bank-rec-to" />
              </label>
              <div className="flex-1" />
              <Button size="sm" variant="ghost" className="rounded-sm"
                disabled={page <= 1 || loadingTxns}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="xero-bank-rec-prev">
                <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Newer
              </Button>
              <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200">Page {page}</Badge>
              <Button size="sm" variant="ghost" className="rounded-sm"
                disabled={loadingTxns || txns.length < 100}
                onClick={() => setPage((p) => p + 1)}
                data-testid="xero-bank-rec-next">
                Older <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </CardContent>
          </Card>

          {loadingTxns ? (
            <Card className="rounded-sm border-zinc-200"><CardContent className="p-12 text-center">
              <Loader2 className="h-6 w-6 animate-spin inline text-[#13B5EA]" />
            </CardContent></Card>
          ) : grouped.length === 0 ? (
            <Card className="rounded-sm border-zinc-200" data-testid="xero-bank-rec-empty">
              <CardContent className="p-12 text-center text-sm text-zinc-500 italic">
                No transactions in this range for {selectedAccount?.name || "this account"}.
              </CardContent>
            </Card>
          ) : (
            grouped.map(([day, items]) => {
              const dayTotal = items.reduce((s, t) => {
                const sign = (t.type || "").startsWith("SPEND") ? -1 : 1;
                return s + sign * (Number(t.total) || 0);
              }, 0);
              return (
                <Card key={day} className="rounded-sm border-zinc-200"
                  data-testid={`xero-bank-rec-day-${day}`}>
                  <CardContent className="p-0">
                    <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between">
                      <div className="font-display font-bold text-sm">
                        {new Date(day).toLocaleDateString(undefined,
                          { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                      </div>
                      <div className="text-xs font-mono">
                        Day total:{" "}
                        <span className={dayTotal >= 0 ? "text-emerald-700" : "text-red-700"}>
                          {dayTotal >= 0 ? "+" : ""}{dayTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    {items.map((t) => {
                      const isSpend = (t.type || "").startsWith("SPEND");
                      const amt = (isSpend ? "-" : "+") + Number(t.total || 0).toFixed(2);
                      return (
                        <div key={t.transaction_id}
                          className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-4 py-2.5 border-b border-zinc-100 last:border-b-0 items-center text-sm hover:bg-zinc-50"
                          data-testid={`xero-bank-rec-txn-${t.transaction_id}`}>
                          <Badge className={`rounded-sm text-[9px] ${
                            isSpend ? "bg-red-100 text-red-800 border-red-200"
                                    : "bg-emerald-100 text-emerald-800 border-emerald-200"
                          }`}>
                            {t.type}
                          </Badge>
                          <div className="min-w-0">
                            <div className="truncate font-medium">
                              {t.contact_name || <span className="italic text-zinc-500">No contact</span>}
                            </div>
                            <div className="text-[10px] text-zinc-500 font-mono truncate">
                              {t.reference || ""} {t.line_count > 0 && `· ${t.line_count} line${t.line_count === 1 ? "" : "s"}`}
                            </div>
                          </div>
                          <div className="text-xs">
                            {t.is_reconciled
                              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                              : <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
                          </div>
                          <div className={`font-mono text-sm font-semibold whitespace-nowrap ${
                            isSpend ? "text-red-700" : "text-emerald-700"
                          }`}>
                            {amt} {t.currency || ""}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
